import React, { useState } from 'react';
import API from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { DOCUMENT_CATEGORIES, CONFIDENTIALITY_LEVELS, SENSITIVITY_FLAGS } from '../types/caseForm';

interface Props {
  caseId: string;
  onUploaded: () => void;
  onCancel: () => void;
}

// The backend caps the stored description at 50 characters, and the sensitivity prefixes the
// edit page prepends count toward it — so the limit is enforced on the full string here.
const DESCRIPTION_MAX = 50;

// Upload one document to a case from the detail page. Same fields and same request shape as the
// edit page's file step, so documents look identical whichever way they were added.
const AddDocumentForm: React.FC<Props> = ({ caseId, onUploaded, onCancel }) => {
  const { showToast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [authorSource, setAuthorSource] = useState('');
  const [language, setLanguage] = useState('');
  const [confidentiality, setConfidentiality] = useState('INTERNAL');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const prefix = warnings.map((w) => SENSITIVITY_FLAGS.find((f) => f.value === w)?.prefix).filter(Boolean).join(' ');
  const fullDescription = prefix ? `${prefix} ${description.trim() || file?.name || ''}` : (description.trim() || file?.name || '');
  const tooLong = fullDescription.length > DESCRIPTION_MAX;
  const canSubmit = !!file && !!category && !!documentDate && fullDescription.trim().length > 0 && !tooLong && !saving;

  const toggleWarning = (v: string) => setWarnings((ws) => (ws.includes(v) ? ws.filter((w) => w !== v) : [...ws, v]));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !file) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append('document', file); // the route is multer .single('document')
      body.append('category', category);
      body.append('description', fullDescription);
      body.append('documentDate', documentDate);
      if (authorSource.trim()) body.append('authorSource', authorSource.trim());
      if (language.trim()) body.append('language', language.trim());
      body.append('confidentiality', confidentiality);
      if (warnings.length > 0) body.append('sensitivityWarnings', JSON.stringify(warnings));
      await API.post(`/api/documents/case/${caseId}`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('success', 'Document uploaded');
      onUploaded();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to upload document');
    } finally {
      setSaving(false);
    }
  };

  const input = 'mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-emerald-500 focus:ring-emerald-500';
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-400';

  return (
    <form onSubmit={submit} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3 bg-gray-50 dark:bg-gray-900/40">
      <div>
        <label className={label} htmlFor="add-doc-file">File <span className="text-red-500">*</span></label>
        <input
          id="add-doc-file"
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="mt-1 block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-white hover:file:bg-emerald-700"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label} htmlFor="add-doc-category">Category <span className="text-red-500">*</span></label>
          <select id="add-doc-category" value={category} onChange={(e) => setCategory(e.target.value)} className={input}>
            <option value="">Select…</option>
            {DOCUMENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={label} htmlFor="add-doc-date">Date of document <span className="text-red-500">*</span></label>
          <input id="add-doc-date" type="date" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} className={input} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="add-doc-description">
          Description <span className="text-red-500">*</span>
          <span className={`ml-2 ${tooLong ? 'text-red-600' : 'text-gray-400'}`}>{fullDescription.length}/{DESCRIPTION_MAX}</span>
        </label>
        <input
          id="add-doc-description"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={file?.name || 'Short description'}
          className={input}
        />
        {prefix && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Stored as: {fullDescription}</p>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label} htmlFor="add-doc-author">Author / source</label>
          <input id="add-doc-author" type="text" value={authorSource} onChange={(e) => setAuthorSource(e.target.value)} className={input} />
        </div>
        <div>
          <label className={label} htmlFor="add-doc-language">Language</label>
          <input id="add-doc-language" type="text" value={language} onChange={(e) => setLanguage(e.target.value)} className={input} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="add-doc-confidentiality">Confidentiality</label>
        <select id="add-doc-confidentiality" value={confidentiality} onChange={(e) => setConfidentiality(e.target.value)} className={input}>
          {CONFIDENTIALITY_LEVELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <fieldset>
        <legend className={label}>Sensitivity warnings</legend>
        <div className="mt-1 space-y-1">
          {SENSITIVITY_FLAGS.map((f) => (
            <label key={f.value} className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={warnings.includes(f.value)} onChange={() => toggleWarning(f.value)} className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
              <span>{f.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} disabled={saving} className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={!canSubmit} className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
          {saving ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  );
};

export default AddDocumentForm;
